import React, { useCallback, useState, useEffect } from "react";
import { Footer } from "../Footer";
import { Header } from "../Header";
import { useDropzone } from "react-dropzone";
import { postImageExpand } from "../../api/apiClient";
import { ExpandResult } from "./ResponsePage/ExpandResult";
import { GlobalDragOverlay } from "../../utils/GlobalDragOverlay";
import { useAuth } from "../../AuthProvider";
import { CloudUpload, Expand, Upload } from "lucide-react";

// Configuration for pixel options based on expand direction
const PIXEL_OPTIONS_CONFIG = {
  all: [
    { value: "128", label: "128 px" },
    { value: "256", label: "256 px" },
  ],
  default: [
    // For 'top', 'bottom', 'left', 'right'
    { value: "128", label: "128 px" },
    { value: "256", label: "256 px" },
    { value: "512", label: "512 px" },
  ],
};

export const ImageExpand = () => {
  const [image, setImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandDirection, setExpandDirection] = useState("top");
  // Initialize with a valid string option value
  const [expandPixels, setExpandPixels] = useState("128");

  const { currentUser } = useAuth();

  // Determine current pixel options based on expandDirection
  const currentPixelOptions =
    expandDirection === "all"
      ? PIXEL_OPTIONS_CONFIG.all
      : PIXEL_OPTIONS_CONFIG.default;

  // Effect to ensure expandPixels is valid for the current expandDirection
  useEffect(() => {
    const isCurrentPixelValueValid = currentPixelOptions.some(
      (option) => option.value === expandPixels
    );

    if (!isCurrentPixelValueValid) {
      // If current expandPixels is not valid for the new direction,
      // set it to the first valid option for that direction.
      setExpandPixels(currentPixelOptions[0].value);
    }
  }, [expandDirection, expandPixels, currentPixelOptions]); // Dependencies

  const processImage = async (imageData) => {
    try {
      const base64String = imageData.split(",")[1];
      setLoading(true);

      const headers = {};
      if (currentUser) {
        headers["Authorization"] = `Bearer ${await currentUser.getIdToken()}`;
        headers["X-User-Id"] = currentUser.uid;
      }

      const response = await postImageExpand({
        image: base64String,
        direction: expandDirection,
        pixels: parseInt(expandPixels),
        headers,
      });

      if (response.data?.image) {
        setProcessedImage(`data:image/png;base64,${response.data.image}`);
      } else if(response.data && response.data.error_code === 2){
          // Specific handling for plan upgrade prompt
          alert("You have exceeded your current plan's monthly limits for image expansion usage. Please upgrade your plan.");
      } else {
        console.error("Invalid response format:", response);
        alert("Failed to process image. Please try again.");
      }
    } catch (error) {
      console.error("Error processing image:", error);
      alert(`Error processing image: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const imageUrl = reader.result;
        setImage(imageUrl);
      };
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*",
    multiple: false,
  });

  useEffect(() => {
    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    document.addEventListener("dragenter", handleDragEnter);
    document.addEventListener("dragleave", handleDragLeave);
    document.addEventListener("dragover", handleDragOver);
    document.addEventListener("drop", handleDrop);

    return () => {
      document.removeEventListener("dragenter", handleDragEnter);
      document.removeEventListener("dragleave", handleDragLeave);
      document.removeEventListener("dragover", handleDragOver);
      document.removeEventListener("drop", handleDrop);
    };
  }, []);

  const handleGlobalDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0 && files[0].type.startsWith("image/")) {
      const reader = new FileReader();
      reader.readAsDataURL(files[0]);
      reader.onload = () => {
        const imageUrl = reader.result;
        setImage(imageUrl);
      };
    }
  };

  const handleGenerateClick = async (e) => {
    e?.preventDefault();
    if (!image) {
      alert("Please upload an image first");
      return;
    }

    try {
      await processImage(image);
    } catch (error) {
      console.error("Error in handleGenerateClick:", error);
    }
  };

  if (processedImage) {
    return (
      <ExpandResult
        processedImage={processedImage}
        originalImage={image}
        onBackClick={() => {
          setProcessedImage(null);
          setImage(null);
        }}
        onNewUpload={() => {
          setProcessedImage(null);
          setImage(null);
        }}
      />
    );
  }

  return (
    <GlobalDragOverlay onGlobalDrop={handleGlobalDrop}>
      <div className="flex-1 h-full overflow-auto relative z-10 bg-gradient-to-b from-slate-50 to-white">
        <Header title={"AI Image Expander"} />

        {/* Upload Section */}
        <section id="try-now" className="py-5 relative">
        
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
           <div className=" mb-5">
              <h2 className="inline-block  text-[24px] rounded-full text-xs font-semibold text-[#121212]">
                Expand Your Image Seamlessly
              </h2>
              
              <p className="text-base sm:text-lg text-[#697586]">
              Add space around your visuals without losing detail. Perfect for banners, social posts, or reframing your composition with AI-generated edges.
              </p>

            <div>
              <div className="w-full bg-[#FFFFFF] h-[600px] rounded-[16px] mt-5 border-[1px] border-[#EEF2F6] overflow-hidden p-4">
                <div className="flex items-center justify-start gap-2">
                  <Expand size={24} className="text-indigo-600" />
                  
                  <h3 className="text-xl font-semibold mb-4 text-gray-800 mt-4">
                    Image Expander
                  </h3>
                </div>

                  <div
                     className={`w-full border-2 flex items-center flex-col h-[500px] justify-center gap-4 bg-[#F8FAFC] ${
                      isDragActive
                        ? "border-indigo-500 bg-indigo-50"
                        : "border-indigo-300"
                    } border-dashed rounded-lg transition-all duration-300 p-6 sm:p-8 md:p-12`}
                    {...getRootProps()}
                  >
                    <input {...getInputProps()} />

                    {image ? (
                      <div className="flex flex-col items-center gap-4 sm:gap-6 w-full h-full">
                        <div className="relative group flex-1 w-full flex items-center justify-center">
                          <img
                            src={image}
                            alt="Uploaded"
                            className="max-w-full max-h-64 sm:max-h-80 md:max-h-96 object-contain rounded-lg shadow-md"
                          />
                          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity duration-300 rounded-lg">
                            <p className="text-white font-medium text-sm sm:text-base">
                              Click Generate to expand this image
                            </p>
                          </div>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setImage(null);
                          }}
                          className="bg-white border border-indigo-500 text-indigo-700 px-4 py-1 sm:px-6 sm:py-2 rounded-full hover:bg-indigo-50 transition-all duration-300 mt-2 sm:mt-4 text-sm sm:text-base"
                        >
                          Upload Another Image
                        </button>
                      </div>
                    ) : (
                      <>
                         <div className="flex items-center justify-center p-1 w-[40px] text-[#697586] h-[40px] bg-white rounded-md">
                        <CloudUpload size={24} />
                      </div>

                      <h3 className="text-md sm:text-lg font-medium text-gray-800 text-center">
                        {isDragActive
                          ? "Drop your image here"
                          : "Drag and drop a photo file here to upload"}
                      </h3>

                      <button className="bg-[#FFFFFF] text-gray-800 px-4 py-2 flex items-center justify-center gap-2 rounded-[8px] text-sm sm:text-base font-medium border-[1px] border-[#CDD5DF] transition-all duration-300 transform hover:-translate-y-1">
                        <Upload size={16} /> <span>Upload Image</span>
                      </button>

                      <p className="text-xs sm:text-sm text-gray-500 text-center max-w-md">
                        Supported file formats: JPG, PNG, GIF, WebP- Maximum
                        file size: 5 MB
                      </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Expansion Controls */}
              <div className="w-full flex flex-col md:flex-row items-center justify-between pr-20 gap-4 mt-4 mb-6 sm:mb-10 mx-4 sm:mx-10">
                <div className="w-full md:w-1/3">
                  <label
                    htmlFor="expandDirection"
                    className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                  >
                    Expand Direction
                  </label>
                  <select
                    id="expandDirection"
                    value={expandDirection}
                    onChange={(e) => setExpandDirection(e.target.value)}
                    className="w-full px-3 py-1 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  >
                    <option value="top">Top</option>
                    <option value="bottom">Bottom</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="all">All</option>
                  </select>
                </div>

                <div className="w-full md:w-1/3">
                  <label
                    htmlFor="expandPixels"
                    className="block text-xs sm:text-sm font-medium text-gray-700 mb-1"
                  >
                    Pixels to Expand
                  </label>
                  <select
                    id="expandPixels"
                    value={expandPixels}
                    onChange={(e) => setExpandPixels(e.target.value)}
                    className="w-full p-3 py-1 sm:px-4 sm:py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 text-sm sm:text-base"
                  >
                    {currentPixelOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="w-full md:w-1/3 flex items-end mt-3 sm:mt-5">
                  <button
                    onClick={handleGenerateClick}
                    disabled={loading || !image}
                    className={`w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-2 sm:px-6 sm:py-2 rounded-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm sm:text-base ${
                      loading ? "opacity-70 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-t-2 border-b-2 border-white"></div>
                        Processing...
                      </>
                    ) : (
                      "Generate"
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

       
      </div>
    </GlobalDragOverlay>
  );
};
